import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';

import { useTranslation, Trans } from 'react-i18next';

// ui
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import { Hidden } from '@material-ui/core';

// icons
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import MobileStepper from '@material-ui/core/MobileStepper';

// components
import SwipeableViews from 'react-swipeable-views';
import { autoPlay } from 'react-swipeable-views-utils';

// Utils
import formatNumber from '../../../utils/number/Number';

// styles
import { styles } from './style';

const useStyles = makeStyles(styles);

const AutoPlaySwipeableViews = autoPlay(SwipeableViews);

const slideSteps = [
  {
    label: <Trans>ฝนสูงสุดวันนี้</Trans>,
    value: '0',
    unit: <Trans>(มม.)</Trans>,
  },
  {
    label: <Trans>ฝนสะสม 3 วัน</Trans>,
    value: '35',
    unit: <Trans>(มม.)</Trans>,
  },
  {
    label: <Trans>ฝนสะสม 7 วัน</Trans>,
    value: '58.7',
    unit: <Trans>(มม.)</Trans>,
  },
  {
    label: <Trans>อุณหภูมิในรอบสัปดาห์</Trans>,
    labelMax: <Trans>สูงสุด</Trans>,
    valueMax: '58.7',
    iconUp: <ArrowDropUpIcon style={{ color: '#ff0000', marginTop: 8 }} />,
    labelMin: <Trans>ต่ำสุด</Trans>,
    iconDown: <ArrowDropDownIcon style={{ marginTop: 5 }} />,
    degree: <FiberManualRecordIcon style={{ fontSize: 10, marginTop: 5, position: 'absolute' }} />,
    valueMin: '37',

  },
  {
    label: <Trans>ปริมาณน้ำใช้การรวม เขื่อนหลัก วันนี้ปีที่ผ่านมา</Trans>,
    value: '7,335',
    valuePer: '(73%)',
  },
];

export default function TopBarDatalists(props) {
  const { t } = useTranslation();
  const classes = useStyles();
  const theme = useTheme();

  const { currentArea } = props;

  const [error, setError] = useState('');
  const [state, setState] = useState({
    datasRain1d: {},
    datasRain3d: {},
    datasRain7d: {},
    datasRainMonth: {},
    datasTemperature: {},
    datasDam: {},
  });

  const [activeStep, setActiveStep] = React.useState(0);
  const maxSteps = slideSteps.length;

  const handleStepChange = (step) => {
    setActiveStep(step);
  };

  useEffect(() => {
    const fetchData = async () => {
      // get related dam id for curren province
      let listDam = '';

      if (currentArea.code !== '0') {
        fetch(`${process.env.REACT_APP_API_URL}/provinces/dam?province_id=${currentArea.code}`)
          .then((response) => {
            if (response.ok) {
              return response.json();
            }

            return null;
          })
          .then((result) => {
            let listDamDaily = [];
            let listDamDailyNear = [];

            if (result.data.dam_daily) {
              listDamDaily = result.data.dam_daily
                .filter((d) => d.agency.id === 12)
                .map((value) => value.dam.id);
            }

            if (result.data.dam_daily_near) {
              listDamDailyNear = result.data.dam_daily_near
                .filter((d) => d.agency.id === 12)
                .map((value) => value.dam.id);
            }

            listDam = [...listDamDaily, ...listDamDailyNear].join(',');

            let mediumDamCondition = '';
            if (result.data.dam_medium) {
              // medium dam ไม่มี mapping medium dam near ใช้ medium dam ได้เลย
              const listMediumDamNear = result.data.dam_medium.map((entity) => entity.dam.id).join(',');
              mediumDamCondition = `&mediumdam_id=${listMediumDamNear}`;
            }

            // get data
            const lastYear = moment().subtract(1, 'year').format('YYYY-MM-DD');
            Promise.all([
              fetch(`${process.env.REACT_APP_API_URL}/provinces/rain1d?province_code=${currentArea.code}`),
              fetch(`${process.env.REACT_APP_API_URL}/provinces/rain3d?province_code=${currentArea.code}`),
              fetch(`${process.env.REACT_APP_API_URL}/provinces/rain7d?province_code=${currentArea.code}`),
              fetch(`${process.env.REACT_APP_API_URL}/provinces/rainfall_month?province_code=${currentArea.code}`),
              fetch(`${process.env.REACT_APP_API_URL}/provinces/temperature_maxmin_thisweek?province_code=${currentArea.code}`),
              fetch(`${process.env.REACT_APP_API_URL}/provinces/dam_uses_water?dam_id=${listDam}${mediumDamCondition}&start_date=${lastYear}&end_date=${lastYear}`),
            ])
              .then(([res1, res2, res3, res4, res5, res6]) => Promise.all([
                res1.json(),
                res2.json(),
                res3.json(),
                res4.json(),
                res5.json(),
                res6.json(),
              ]))
              .then(([data1, data2, data3, data4, data5, data6]) => {
                setState({
                  datasRain1d: data1.data.length === 0 ? { rain_1d: '-' } : data1.data[0],
                  datasRain3d: data2.data.length === 0 ? { rain_3d: '-' } : data2.data[0],
                  datasRain7d: data3.data.length === 0 ? { rain_7d: '-' } : data3.data[0],
                  datasRainMonth: data4.data.length === 0 ? '-' : data4.data[0],
                  datasTemperature: data5.data.length === 0 ? { max_temperature: '-', min_temperature: '-' } : data5.data[0],
                  datasDam: data6.data[0],
                });
              })
              .catch((err) => setError(err));
          })
          .catch((err) => setError(err));
      }

      return null;
    };

    fetchData();
  }, [currentArea]);

  return (
    <>
      {error && <div>Error Occured</div>}
      <Hidden lgUp>
        <Grid container justifycontent="center">
          <Grid item xs={12} align="center">
            <AutoPlaySwipeableViews
              axis={theme.direction === 'rtl' ? 'x-reverse' : 'x'}
              index={activeStep}
              onChangeIndex={handleStepChange}
              enableMouseEvents
            >
              {slideSteps.map((step, index) => (
                <div key={step.label}>
                  <Grid container direction="column" justifycontent="center">
                    {Math.abs(activeStep - index) <= 2 ? (
                      <Box>
                        <Box display="block"><Typography alignitems="center" className={classes.titleSub} display="block">{step.label}</Typography></Box>
                        <Box display="block">
                          <Box display="flex" justifyContent="center" alignItems="center">
                            <Typography alignitems="center" className={classes.value}>
                              {step.value}
                            </Typography>
                            <Typography alignitems="center" className={classes.valuePer}>
                              {step.valuePer}
                            </Typography>
                            <Typography alignitems="center" className={classes.titleSub} display="inline">
                              {step.labelMax}
                            </Typography>
                            <Typography alignitems="center" display="inline">
                              {step.iconUp}
                            </Typography>
                            <Typography alignitems="center" className={classes.titleSub}>
                              {step.unit}
                            </Typography>
                            <Typography alignitems="center" className={classes.valueAll}>
                              {step.valueMax}
                              {step.degree}
                            </Typography>
                            <Box pl={3}>
                              <Typography alignitems="center" className={classes.titleSub}>
                                {step.labelMin}
                              </Typography>
                            </Box>
                            <Typography alignitems="center" display="inline">
                              {step.iconDown}
                            </Typography>
                            <Typography alignitems="center" className={classes.valueAll}>
                              {step.valueMin}
                              {step.degree}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    ) : null}
                  </Grid>
                </div>
              ))}
            </AutoPlaySwipeableViews>
            <Box mb={1} display="flex" justifyContent="center" alignItems="center">
              <MobileStepper
                steps={maxSteps}
                position="static"
                variant="dots"
                activeStep={activeStep}
              />
            </Box>
          </Grid>
        </Grid>
      </Hidden>

      <Hidden mdDown>
        <Box mb={1} display="flex" alignItems="center" flexWrap="nowrap" style={{ width: '100%' }}>
          <Box display="flex" width="13%" alignItems="center" justifyContent="center" className={classes.bodyBox}>
            <Typography className={classes.titleSub} display="inline" noWrap>{t('ฝนสูงสุดวานนี้')}</Typography>
            <Typography className={classes.value} display="inline" id="rain_max">
              {state.datasRain1d.rain_1d}
            </Typography>
            <Typography className={classes.titleSub} display="inline">{t('(มม.)')}</Typography>
          </Box>
          <Box display="flex" width="14%" alignItems="center" justifyContent="center" className={classes.bodyBox}>
            <Typography className={classes.titleSub} display="inline" noWrap>{t('ฝนสะสม 3 วัน')}</Typography>
            <Typography className={classes.value} display="inline" id="rain_3_day">
              {!isEmpty(state.datasRain3d)
                ? state.datasRain3d.rain_3d
                : '--'}
            </Typography>
            <Typography className={classes.titleSub} display="inline">{t('(มม.)')}</Typography>
          </Box>
          <Box display="flex" width="15%" alignItems="center" justifyContent="center" className={classes.bodyBox}>
            <Typography className={classes.titleSub} display="inline" noWrap>{t('ฝนสะสม 7 วัน')}</Typography>
            <Typography className={classes.value} display="inline" id="rain_7_day">
              {!isEmpty(state.datasRain7d)
                ? state.datasRain7d.rain_7d
                : '--'}
            </Typography>
            <Typography className={classes.titleSub} display="inline">{t('(มม.)')}</Typography>
          </Box>
          <Box display="flex" width="33%" alignItems="center" justifyContent="center" className={classes.bodyBox}>
            <Typography className={classes.titleWeek} display="inline">{t('อุณหภูมิในรอบสัปดาห์  สูงสูด')}</Typography>
            <ArrowDropUpIcon style={{ color: '#ff0000', padding: 0 }} />
            <Typography className={classes.value} display="inline" id="temperature_max">
              {state.datasTemperature.max_temperature}
              {' '}
              <FiberManualRecordIcon className={classes.textTemp} />
            </Typography>
            <Box ml={2}><Typography className={classes.titleWeek} display="inline">{t('ต่ำสุด')}</Typography></Box>
            <ArrowDropDownIcon />
            <Typography className={classes.value} display="inline" id="temperature_min">
              {state.datasTemperature.min_temperature}
              {' '}
              <FiberManualRecordIcon className={classes.textTemp} />
            </Typography>
          </Box>
          <Box display="flex" width="25%" alignItems="center" className={classes.bodyBox}>
            <Box align="left" flexGrow={1}>
              <Typography className={classes.titleWeek} display="block">{t('ปริมาตรน้ำใช้การของแหล่งน้ำในจังหวัด')}</Typography>
              <Typography className={classes.titleWeek} display="block">{t('วันนี้ปีที่ผ่านมา (ล้าน ลบ.ม/% น้ำใช้การ)')}</Typography>
            </Box>
            <Box p={1} id="datasDamUsPercent">
              {state.datasDam !== undefined
                && (
                <>
                  <Typography className={classes.valueAll} display="inline">
                    {formatNumber(state.datasDam.dam_uses_water)}
                  </Typography>
                  <Box pl={1} className={classes.valuePer} display="inline">
                    (
                    {state.datasDam.dam_uses_water_percent}
                    %)
                  </Box>
                </>
                )}

              {state.datasDam === undefined
                && (
                <>
                  <Typography className={classes.valueAll} display="inline">
                    n/a
                  </Typography>
                </>
                )}
            </Box>
          </Box>
        </Box>
      </Hidden>
    </>
  );
}

TopBarDatalists.propTypes = {
  currentArea: PropTypes.instanceOf(Object).isRequired,
};
